/**
 * Centralized image-shape codec table.
 *
 * Flows hold their uploaded image(s) in different shapes: `UploadedSource[]`
 * (Product Creative / Profile Photo), a single `UploadedSource` (Baseball
 * Trend / Talking Avatar), a base64 data-URL `string` (Text/Frames to Video,
 * Product Catalog, Image Generator's source), an `{id,url}[]` list (Image
 * Generator reference images) or a raw `Blob` (Image Generator inpaint mask).
 *
 * A codec turns any of these into the `{id,file}` entries `flowImageStore`
 * persists, plus a small JSON-safe `meta` remainder (ordering, already-remote
 * URLs) that rides in localStorage. On restore it rebuilds the value in its
 * ORIGINAL representation so each flow's `generate()` is byte-for-byte
 * unchanged (UploadedSource flows get a `File` + fresh blob preview; data-URL
 * flows get a data-URL string back; the mask gets a Blob).
 *
 * Adding support for a NEW image shape is a one-place change here — never per
 * flow. Detection is by value shape, so a flow just lists the key.
 */

import { base64ToBlob } from "./base64";
import type { FlowImageEntry } from "./flowImageStore";

/** A codec's name is persisted so restore can pick the same codec back. */
export type CodecName =
  | "uploaded-source-list"
  | "uploaded-source"
  | "chat-reference-list"
  | "url-object-list"
  | "data-url-string"
  | "blob";

interface SplitResult {
  /** Binary payload → IndexedDB. */
  entries: FlowImageEntry[];
  /** JSON-safe remainder (order, already-remote URLs) → localStorage. */
  meta: unknown;
}

interface ImageCodec {
  name: CodecName;
  /** Does this codec handle this value's shape? */
  match: (value: unknown) => boolean;
  split: (value: unknown) => SplitResult;
  merge: (
    entries: FlowImageEntry[],
    meta: unknown,
  ) => unknown | Promise<unknown>;
}

const SINGLE = "_"; // slot id for single-value codecs

const isDataUrl = (v: unknown): v is string =>
  typeof v === "string" && v.startsWith("data:");

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function fileFromDataUrl(dataUrl: string, name: string): File {
  const blob = base64ToBlob(dataUrl);
  return new File([blob], name, { type: blob.type });
}

/** `fileFromDataUrl` that returns `null` instead of throwing on corrupt base64
 *  (e.g. a truncated localStorage value). Lets split() drop the bad entry and
 *  keep going rather than crash the route during a render-triggered persist. */
function safeFileFromDataUrl(dataUrl: string, name: string): File | null {
  try {
    return fileFromDataUrl(dataUrl, name);
  } catch (err) {
    console.warn("flowImageCodecs: dropping corrupt base64 entry", name, err);
    return null;
  }
}

/** Minimal structural shapes we detect (kept loose on purpose). */
interface UploadedSourceLike {
  id: string;
  publicUrl: string;
  previewUrl: string;
  file?: File;
}
interface UrlObjectLike {
  id: string;
  url: string;
}
interface ChatReferenceLike {
  id: string;
  url: string;
  /** Data URL of the image bytes (the heavy field). */
  base64: string;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;
const looksLikeUploadedSource = (v: unknown): v is UploadedSourceLike =>
  isObj(v) && typeof v.previewUrl === "string";
const looksLikeUrlObject = (v: unknown): v is UrlObjectLike =>
  isObj(v) && typeof v.url === "string" && typeof v.id === "string";
const looksLikeChatReference = (v: unknown): v is ChatReferenceLike =>
  isObj(v) &&
  typeof v.id === "string" &&
  typeof v.url === "string" &&
  typeof v.base64 === "string";

// ── Codecs ────────────────────────────────────────────────────────────────

/** `UploadedSource[]` — Product Creative, Profile Photo. */
const uploadedSourceListCodec: ImageCodec = {
  name: "uploaded-source-list",
  match: (v) =>
    Array.isArray(v) && (v.length === 0 || looksLikeUploadedSource(v[0])),
  split: (v) => {
    const list = v as UploadedSourceLike[];
    const entries: FlowImageEntry[] = [];
    const meta = list.map((item) => {
      if (item.file) {
        entries.push({ id: item.id, file: item.file });
        return { id: item.id, hasFile: true, publicUrl: "" };
      }
      return { id: item.id, hasFile: false, publicUrl: item.publicUrl };
    });
    return { entries, meta };
  },
  merge: (entries, meta) => {
    const order = (meta as Array<{
      id: string;
      hasFile: boolean;
      publicUrl: string;
    }>) ?? [];
    const byId = new Map(entries.map((e) => [e.id, e.file]));
    return order
      .map((m) => {
        if (m.hasFile) {
          const file = byId.get(m.id);
          if (!file) return null;
          return {
            id: m.id,
            publicUrl: "",
            previewUrl: URL.createObjectURL(file),
            file,
          };
        }
        if (m.publicUrl) {
          return { id: m.id, publicUrl: m.publicUrl, previewUrl: m.publicUrl };
        }
        return null;
      })
      .filter(Boolean);
  },
};

/** A single `UploadedSource` — Baseball Trend, Talking Avatar. */
const uploadedSourceCodec: ImageCodec = {
  name: "uploaded-source",
  match: (v) => !Array.isArray(v) && looksLikeUploadedSource(v),
  split: (v) => {
    const s = v as UploadedSourceLike;
    if (s.file) {
      return { entries: [{ id: SINGLE, file: s.file }], meta: { id: s.id } };
    }
    return { entries: [], meta: { id: s.id, publicUrl: s.publicUrl } };
  },
  merge: (entries, meta) => {
    const m = (meta ?? {}) as { id?: string; publicUrl?: string };
    const id = m.id ?? SINGLE;
    if (entries[0]) {
      return {
        id,
        publicUrl: "",
        previewUrl: URL.createObjectURL(entries[0].file),
        file: entries[0].file,
      };
    }
    if (m.publicUrl) {
      return { id, publicUrl: m.publicUrl, previewUrl: m.publicUrl };
    }
    return null;
  },
};

/** `ChatReferenceImage[]` ({id,url,base64}) — Image Director Chat reference
 *  images. `base64` is a (potentially multi-MB) data URL → it MUST go to
 *  IndexedDB or one image blows the localStorage quota and silently drops the
 *  whole record (conversation draft included). Matched before
 *  `urlObjectListCodec` because a ChatReference also has id+url. */
const chatReferenceListCodec: ImageCodec = {
  name: "chat-reference-list",
  match: (v) =>
    Array.isArray(v) && v.length > 0 && v.every(looksLikeChatReference),
  split: (v) => {
    const list = v as ChatReferenceLike[];
    const entries: FlowImageEntry[] = [];
    const meta = list.map((item) => {
      const file = isDataUrl(item.base64)
        ? safeFileFromDataUrl(item.base64, item.id)
        : null;
      if (file) {
        entries.push({ id: item.id, file });
        return { id: item.id, url: item.url, hasB64: true };
      }
      // Already-remote (https) reference, OR corrupt base64 we dropped — keep
      // the remote url and any non-decodable base64 as-is rather than crash.
      return { id: item.id, url: item.url, hasB64: false, base64: item.base64 };
    });
    return { entries, meta };
  },
  merge: async (entries, meta) => {
    const order = (meta as Array<{
      id: string;
      url: string;
      hasB64: boolean;
      base64?: string;
    }>) ?? [];
    const byId = new Map(entries.map((e) => [e.id, e.file]));
    const out: ChatReferenceLike[] = [];
    for (const m of order) {
      let base64 = m.base64 ?? "";
      if (m.hasB64) {
        const file = byId.get(m.id);
        if (!file) continue; // bytes lost (IDB miss) — drop, per no-fallbacks
        base64 = await blobToDataUrl(file);
      }
      out.push({ id: m.id, url: m.url, base64 });
    }
    return out;
  },
};

/** `{id,url}[]` where url may be a data URL — Image Generator references. */
const urlObjectListCodec: ImageCodec = {
  name: "url-object-list",
  match: (v) =>
    Array.isArray(v) && v.length > 0 && v.every(looksLikeUrlObject),
  split: (v) => {
    const list = v as UrlObjectLike[];
    const entries: FlowImageEntry[] = [];
    const meta = list.map((item) => {
      const file = isDataUrl(item.url)
        ? safeFileFromDataUrl(item.url, item.id)
        : null;
      if (file) {
        entries.push({ id: item.id, file });
        return { id: item.id, binary: true };
      }
      // Remote url, OR corrupt data URL we dropped — keep the url as-is.
      return { id: item.id, binary: false, url: item.url };
    });
    return { entries, meta };
  },
  merge: async (entries, meta) => {
    const order = (meta as Array<{
      id: string;
      binary: boolean;
      url?: string;
    }>) ?? [];
    const byId = new Map(entries.map((e) => [e.id, e.file]));
    const out: UrlObjectLike[] = [];
    for (const m of order) {
      if (m.binary) {
        const file = byId.get(m.id);
        if (file) out.push({ id: m.id, url: await blobToDataUrl(file) });
      } else if (m.url) {
        out.push({ id: m.id, url: m.url });
      }
    }
    return out;
  },
};

/** A base64 data-URL string (or "" / a remote URL kept as-is). */
const dataUrlStringCodec: ImageCodec = {
  name: "data-url-string",
  match: (v) => typeof v === "string",
  split: (v) => {
    const s = v as string;
    if (isDataUrl(s)) {
      const file = safeFileFromDataUrl(s, SINGLE);
      // Corrupt base64 → drop the bytes; keep the (unusable) string as meta so
      // restore is a no-op string rather than a crash.
      if (file) return { entries: [{ id: SINGLE, file }], meta: null };
      return { entries: [], meta: "" };
    }
    return { entries: [], meta: s }; // "" or an already-remote http URL
  },
  merge: async (entries, meta) => {
    if (entries[0]) return blobToDataUrl(entries[0].file);
    return typeof meta === "string" ? meta : "";
  },
};

/** A raw `Blob` — Image Generator inpaint mask. */
const blobCodec: ImageCodec = {
  name: "blob",
  match: (v) => v instanceof Blob,
  split: (v) => {
    const blob = v as Blob;
    const file =
      blob instanceof File ? blob : new File([blob], SINGLE, { type: blob.type });
    return { entries: [{ id: SINGLE, file }], meta: { type: blob.type } };
  },
  // A File IS a Blob, so returning the File satisfies every Blob consumer.
  merge: (entries) => entries[0]?.file ?? null,
};

// Order matters: more specific shapes first. `blob` precedes nothing relevant
// (UploadedSource is a plain object, not a Blob).
const CODECS: ImageCodec[] = [
  blobCodec,
  uploadedSourceListCodec,
  uploadedSourceCodec,
  chatReferenceListCodec,
  urlObjectListCodec,
  dataUrlStringCodec,
];

const CODECS_BY_NAME = new Map(CODECS.map((c) => [c.name, c]));

/**
 * Split a flow image-field value into binary entries (→ IndexedDB) + JSON
 * meta (→ localStorage). Returns null for null/undefined/unhandled values
 * (nothing to persist — the flow simply restores empty, per no-fallbacks).
 */
export function encodeImageField(
  value: unknown,
): { codec: CodecName; entries: FlowImageEntry[]; meta: unknown } | null {
  if (value == null) return null;
  const codec = CODECS.find((c) => c.match(value));
  if (!codec) return null;
  const { entries, meta } = codec.split(value);
  // Nothing to persist: no binary AND no meaningful remainder. An empty array
  // (e.g. `uploads: []`, `referenceImages: []`) counts as nothing — otherwise
  // it would write a spurious image-meta entry and force the async IndexedDB
  // restore path on every subsequent mount.
  const emptyMeta =
    meta == null ||
    meta === "" ||
    (Array.isArray(meta) && meta.length === 0);
  if (entries.length === 0 && emptyMeta) return null;
  return { codec: codec.name, entries, meta };
}

/** Rebuild a flow image-field value from restored entries + meta. */
export async function decodeImageField(
  codec: CodecName,
  entries: FlowImageEntry[],
  meta: unknown,
): Promise<unknown> {
  const c = CODECS_BY_NAME.get(codec);
  if (!c) return undefined;
  return c.merge(entries, meta);
}
